import { applyApiDocsEnvironment } from '@/utils/apiDocsEnvironment'

const ALLOWED_TAGS = new Set([
  'CODE',
  'STRONG',
  'EM',
  'A',
  'BR',
  'SPAN',
  'B',
  'I',
])

function unwrapElement(node) {
  const parent = node.parentNode
  if (!parent) return
  while (node.firstChild) {
    parent.insertBefore(node.firstChild, node)
  }
  parent.removeChild(node)
}

function sanitizeInlineHtml(element) {
  const clone = element.cloneNode(true)

  clone.querySelectorAll('span').forEach((span) => {
    if (
      span.classList.contains('req-badge') ||
      span.classList.contains('scope-chip')
    ) {
      return
    }
    unwrapElement(span)
  })

  clone.querySelectorAll('*').forEach((node) => {
    node.removeAttribute('class')
    node.removeAttribute('style')

    if (!ALLOWED_TAGS.has(node.tagName)) {
      unwrapElement(node)
    }
  })

  clone.querySelectorAll('a[href^="#"]').forEach((link) => {
    link.setAttribute('data-doc-anchor', link.getAttribute('href').slice(1))
    link.setAttribute('href', '#')
  })

  return clone.innerHTML.trim()
}

function parseTable(tableEl) {
  const rows = [...tableEl.querySelectorAll('tr')].map((row) =>
    [...row.children].map((cell) => {
      const clone = cell.cloneNode(true)
      const requiredBadge = clone.querySelector('.req-badge')
      const required = requiredBadge?.classList.contains('req-yes')
        ? 'yes'
        : requiredBadge?.classList.contains('req-no')
          ? 'no'
          : null
      requiredBadge?.remove()

      return {
        html: sanitizeInlineHtml(clone),
        required,
      }
    }),
  )

  const hasHeader = Boolean(tableEl.querySelector('th'))

  return {
    kind: 'table',
    variant: tableEl.classList.contains('domain-table') ? 'domain' : 'default',
    header: hasHeader ? rows[0] : null,
    rows: hasHeader ? rows.slice(1) : rows,
  }
}

function parseParagraph(el) {
  const scopeChip = el.querySelector('.scope-chip')
  if (scopeChip) {
    const scope = scopeChip.textContent.trim()
    const clone = el.cloneNode(true)
    clone.querySelector('.scope-chip')?.remove()
    return {
      kind: 'scope-note',
      scope,
      html: sanitizeInlineHtml(clone),
    }
  }

  return {
    kind: 'paragraph',
    lead: el.classList.contains('lead'),
    html: sanitizeInlineHtml(el),
  }
}

function parseEndpoint(el) {
  const badge = el.querySelector('.badge')
  const path = el.querySelector('.endpoint-path')
  const methodClass = [...(badge?.classList ?? [])].find((name) =>
    name.startsWith('m-'),
  )
  const method = (methodClass?.replace('m-', '') ?? badge?.textContent ?? 'GET')
    .trim()
    .toUpperCase()

  return {
    kind: 'endpoint',
    method,
    path: path?.textContent?.trim() ?? '',
  }
}

function parseCallout(el) {
  let variant = 'info'
  if (el.classList.contains('warn')) variant = 'warn'
  if (el.classList.contains('danger')) variant = 'danger'

  return {
    kind: 'callout',
    variant,
    html: sanitizeInlineHtml(el),
  }
}

function parseMainBlock(el) {
  const tag = el.tagName?.toLowerCase()

  if (tag === 'h1' || tag === 'h2' || tag === 'h3' || tag === 'h4') {
    return {
      kind: 'heading',
      level: Number(tag.slice(1)),
      id: el.id || null,
      text: el.textContent.trim(),
    }
  }

  if (tag === 'p') return parseParagraph(el)
  if (tag === 'table') return parseTable(el)
  if (tag === 'pre') {
    return {
      kind: 'code',
      code: el.textContent.replace(/\n$/, ''),
    }
  }
  if (tag === 'ul') {
    return {
      kind: 'list',
      items: [...el.querySelectorAll('li')].map((item) =>
        sanitizeInlineHtml(item),
      ),
    }
  }
  if (el.classList?.contains('endpoint-header')) return parseEndpoint(el)
  if (el.classList?.contains('callout')) return parseCallout(el)

  return null
}

export const API_DOC_SOURCES = {
  public: {
    id: 'public',
    path: 'api-documentation.html',
    title: 'API Documentation',
    subtitle: 'Outbound calls & authentication',
  },
  contact: {
    id: 'contact',
    path: 'contact-api-documentation.html',
    title: 'Contact API Documentation',
    subtitle: 'Contact Us — public form endpoint',
  },
}

export function getSectionIcon(sectionId, method) {
  const icons = {
    overview: 'mdi-api',
    auth: 'mdi-shield-key-outline',
    errors: 'mdi-alert-circle-outline',
    'list-campaigns': 'mdi-format-list-bulleted',
    'outbound-call': 'mdi-phone-outgoing-outline',
    'contact-us': 'mdi-email-outline',
    contact: 'mdi-email-outline',
    'org-settings': 'mdi-office-building-cog-outline',
    notes: 'mdi-information-outline',
    'rate-limits': 'mdi-speedometer',
  }
  if (icons[sectionId]) return icons[sectionId]
  if (method === 'GET') return 'mdi-download-outline'
  if (method === 'DELETE') return 'mdi-trash-can-outline'
  if (method === 'PUT') return 'mdi-pencil-outline'
  return 'mdi-webhook'
}

// Pulls the JSON payload out of a documented curl example so the endpoint
// tester has a request body to start from. The docs carry examples only as
// syntax-highlighted text, so this is the one place that turns them back into
// data. Returns '' when the example has no body (GET routes), which the tester
// reads as "nothing to send".
function extractRequestBodyFromCode(code) {
  const text = String(code ?? '')
  // Matches the -d '{...}' argument, taking the outermost braces so nested
  // objects survive.
  const start = text.indexOf("-d '")
  if (start === -1) return ''
  const open = text.indexOf('{', start)
  if (open === -1) return ''
  let depth = 0
  for (let i = open; i < text.length; i += 1) {
    if (text[i] === '{') depth += 1
    else if (text[i] === '}') {
      depth -= 1
      if (depth === 0) {
        const candidate = text.slice(open, i + 1)
        try {
          // Round-tripped so the tester always gets valid, uniformly
          // formatted JSON rather than whatever spacing the docs used.
          return JSON.stringify(JSON.parse(candidate), null, 2)
        } catch {
          return ''
        }
      }
    }
  }
  return ''
}

function enrichSection(section) {
  section.endpoint = section.blocks.find((b) => b.kind === 'endpoint') || null
  section.scopeNote = section.blocks.find((b) => b.kind === 'scope-note') || null
  section.callouts = section.blocks.filter((b) => b.kind === 'callout')
  section.codes = section.blocks.filter((b) => b.kind === 'code')
  if (section.endpoint) {
    // First example that actually carries a payload -- later blocks are
    // response samples, which must not be mistaken for a request.
    const requestExample = section.codes
      .map((b) => extractRequestBodyFromCode(b.code))
      .find((body) => body)
    section.endpoint.requestBody = requestExample || '{}'
    // An endpoint whose path carries a {placeholder} cannot be run as-is --
    // the tester would send the literal "{action_id}" and get a 404. Those
    // need a way to supply the value first, so they stay disabled with a
    // reason rather than offering a button that always fails.
    const hasPathParam = /\{[^}]+\}/.test(section.endpoint.path || '')
    // The tester always sends requestUrl = baseUrl + endpoint.path (see
    // ApiDocsTestDialog's requestUrl computed) against this page's single
    // api.yandle.io base -- fine for a relative path, but an endpoint
    // documented with its own full URL (Create Lead: a different,
    // unauthenticated domain, contact-api.yandle.io) would get naively
    // concatenated onto that base into a garbage URL. Disabled here rather
    // than letting the tester attempt it.
    const isCrossDomain = /^https?:\/\//i.test(section.endpoint.path || '')
    // A body example is no longer required: several endpoints legitimately
    // take no body now that the organisation comes from the access token.
    section.endpoint.testable = !hasPathParam && !isCrossDomain
    section.endpoint.notTestableReason = hasPathParam
      ? 'Endpoints with a path parameter cannot be run from here yet'
      : isCrossDomain
        ? 'This endpoint is on a different domain and API base -- use the curl example instead'
        : ''
  }
  section.tables = section.blocks.filter((b) => b.kind === 'table')
  section.lists = section.blocks.filter((b) => b.kind === 'list')
  section.subheadings = section.blocks.filter(
    (b) => b.kind === 'heading' && b.level > 2,
  )

  const headingIndex = section.blocks.findIndex((b) => b.kind === 'heading')
  const firstParagraph = section.blocks
    .slice(headingIndex + 1)
    .find((b) => b.kind === 'paragraph' && !b.lead)

  section.description =
    firstParagraph?.html ||
    section.blocks.find((b) => b.kind === 'paragraph' && b.lead)?.html ||
    ''

  section.icon = getSectionIcon(
    section.id,
    section.endpoint?.method,
  )
}

export function groupBlocksIntoSections(blocks) {
  const sections = []
  let current = null

  const pushCurrent = () => {
    if (!current) return
    enrichSection(current)
    sections.push(current)
    current = null
  }

  for (const block of blocks) {
    if (block.kind === 'heading' && (block.level === 1 || block.level === 2) && block.id) {
      pushCurrent()
      current = {
        id: block.id,
        title: block.text,
        blocks: [block],
      }
      continue
    }

    if (!current) {
      current = {
        id: 'overview',
        title: 'Overview',
        blocks: [],
      }
    }
    current.blocks.push(block)
  }

  pushCurrent()
  return sections
}

export function parseApiDocumentationHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const navRoot = doc.querySelector('nav.sidebar')
  const mainRoot = doc.querySelector('main')

  const navigation = []
  if (navRoot) {
    for (const child of navRoot.children) {
      if (child.classList.contains('section-title')) {
        navigation.push({
          kind: 'section',
          label: child.textContent.trim(),
        })
      } else if (child.tagName === 'A') {
        const href = child.getAttribute('href') ?? ''
        navigation.push({
          kind: 'link',
          label: child.textContent.trim(),
          id: href.startsWith('#') ? href.slice(1) : href,
        })
      }
    }
  }

  const blocks = []
  if (mainRoot) {
    for (const child of mainRoot.children) {
      if (child.nodeType === Node.COMMENT_NODE) continue
      const block = parseMainBlock(child)
      if (block) {
        blocks.push({
          ...block,
          key: block.id || `${block.kind}-${blocks.length}`,
        })
      }
    }
  }

  const sections = groupBlocksIntoSections(blocks)
  const sectionIds = sections.map((section) => section.id)

  return {
    title:
      doc.querySelector('title')?.textContent?.trim() ||
      'Yandle API Documentation',
    navigation,
    blocks,
    sections,
    sectionIds,
  }
}

export async function loadApiDocumentation({
  force = false,
  source = 'public',
} = {}) {
  const docSource = API_DOC_SOURCES[source] || API_DOC_SOURCES.public
  const DOC_PATH = `${process.env.BASE_URL}${docSource.path}`
  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  const cacheKey = `${docSource.id}:${hostname || 'unknown'}`

  if (
    !loadApiDocumentation._cache?.[cacheKey]
    || force
  ) {
    const response = await fetch(DOC_PATH, { cache: 'no-cache' })
    if (!response.ok) {
      throw new Error(
        source === 'contact'
          ? 'Failed to load Contact API documentation.'
          : 'Failed to load API documentation.',
      )
    }
    const html = await response.text()
    const parsed = parseApiDocumentationHtml(html)
    if (!loadApiDocumentation._cache) {
      loadApiDocumentation._cache = Object.create(null)
    }
    loadApiDocumentation._cache[cacheKey] = applyApiDocsEnvironment(
      parsed,
      hostname,
    )
  }
  return loadApiDocumentation._cache[cacheKey]
}

loadApiDocumentation._cache = null

export function getApiDocumentationEndpointCount(source = 'public') {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  const docSource = API_DOC_SOURCES[source] || API_DOC_SOURCES.public
  const cacheKey = `${docSource.id}:${hostname || 'unknown'}`
  const doc = loadApiDocumentation._cache?.[cacheKey]
  // Fallback only, used before the doc has parsed. Bumped from 12 when
  // GET /campaigns was added -- the real number always comes from the parsed
  // navigation below.
  if (!doc) return source === 'contact' ? 3 : 13
  return doc.navigation.filter((item) => item.kind === 'link').length
}
